import React, { useEffect, useState } from "react";
import { FaRegClipboard } from "react-icons/fa";
import { useBooking } from "../../context/BookingContext";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../../firebase";

interface PenaltyBreakdown {
  dateRange: string;
  timeRange?: string;
  amount: number;
  currency: string;
  description?: string;
}

interface PenaltyInfo {
  allowed: boolean;
  amount?: number;
  currency?: string;
  percentage?: number;
  description?: string;
  penaltyBreakdown?: PenaltyBreakdown[];
}

interface FareRulesResult {
  success: boolean;
  refund?: PenaltyInfo;
  exchange?: PenaltyInfo;
  error?: string;
  message?: string;
}

/** Derive departure/return dates from revalidated flight.data for the API request (one-way, roundtrip, multicity). */
function getDatesFromFlightData(flightData: any): { departureDate: string; returnDate?: string } {
  if (!flightData) return { departureDate: "" };
  // Prefer explicit dates stored during revalidation
  let departureDate = flightData.onwardDepartureDate || "";
  const returnDateExplicit = flightData.returnDepartureDate || undefined;
  if (departureDate && returnDateExplicit !== undefined) {
    return { departureDate, returnDate: returnDateExplicit || undefined };
  }
  // New format: extract from legs[]
  const legs: any[] = flightData?.legs ?? [];
  const outboundLeg = legs.find((l: any) => l.direction === "outbound") ?? legs[0];
  const inboundLeg = legs.find((l: any) => l.direction === "inbound");
  if (!departureDate && outboundLeg?.departure?.dateTime) {
    const dt = outboundLeg.departure.dateTime;
    if (typeof dt === "string") {
      const m = dt.match(/^\d{4}-\d{2}-\d{2}/);
      if (m) departureDate = m[0];
    }
  }
  // Old format fallback: onwardFlight
  if (!departureDate) {
    const onwardDt = flightData?.onwardFlight?.departureDateTime;
    if (onwardDt && typeof onwardDt === "string") {
      const m = onwardDt.match(/^\d{4}-\d{2}-\d{2}/);
      if (m) departureDate = m[0];
    }
    if (!departureDate) {
      const onward = flightData?.onwardFlight?.schedules?.[0]?.departure?.time;
      if (onward) {
        const d = new Date(onward);
        if (!isNaN(d.getTime())) departureDate = d.toISOString().split("T")[0];
      }
    }
  }
  // Multicity fallback
  if (!departureDate && legs.length > 0 && legs[0]?.departure?.dateTime) {
    const dt = legs[0].departure.dateTime;
    if (typeof dt === "string") {
      const m = dt.match(/^\d{4}-\d{2}-\d{2}/);
      if (m) departureDate = m[0];
    }
  }
  let derivedReturn: string | undefined = returnDateExplicit;
  // New format: inbound leg
  if (derivedReturn === undefined && inboundLeg?.departure?.dateTime) {
    const dt = inboundLeg.departure.dateTime;
    if (typeof dt === "string") {
      const m = dt.match(/^\d{4}-\d{2}-\d{2}/);
      if (m) derivedReturn = m[0];
    }
  }
  // Old format fallback: returnFlight
  if (derivedReturn === undefined) {
    const retDt = flightData?.returnFlight?.departureDateTime;
    if (retDt && typeof retDt === "string") {
      const m = retDt.match(/^\d{4}-\d{2}-\d{2}/);
      if (m) derivedReturn = m[0];
    }
    if (!derivedReturn && flightData?.returnFlight?.schedules?.[0]?.departure?.time) {
      const d = new Date(flightData.returnFlight.schedules[0].departure.time);
      if (!isNaN(d.getTime())) derivedReturn = d.toISOString().split("T")[0];
    }
  }
  return { departureDate, returnDate: derivedReturn };
}

interface FareRulesDisplayProps {
  flightData?: any;
  passengerCounts?: { ADT?: number; CHD?: number; INF?: number };
  departureDate?: string;
  returnDate?: string;
  currency?: string;
  /** When present (after revalidation), backend builds fare rules from stored reval; preferred over sending full flightData */
  revalId?: string;
  sessionId?: string;
  userId?: string;
  /** "popup" = button that opens modal (close on outside click); "inline" = always visible */
  displayMode?: "popup" | "inline";
  variant?: "compact" | "full";
  className?: string;
}

const FareRulesDisplay: React.FC<FareRulesDisplayProps> = ({
  currency = "AED",
  displayMode = "inline",
  variant = "full",
  className = "",
}) => {
  const { fareRules: contextFareRules, setFareRules } = useBooking();
  const [data, setData] = useState<FareRulesResult | null>(contextFareRules as FareRulesResult | null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetched, setFetched] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);

  const {
    revalId,
    sessionId,
    flightData,
    passengerCounts,
    departureDate,
    returnDate
  } = useBooking();
  const [user] = useAuthState(auth);

  const userId = user?.uid

  const { departureDate: derivedDep, returnDate: derivedRet } = getDatesFromFlightData(flightData);
  const useRevalIdFlow = Boolean(revalId && sessionId && userId);
  const hasDepDate = Boolean(derivedDep || departureDate);
  const canFetch =
    passengerCounts &&
    Object.keys(passengerCounts).length > 0 &&
    hasDepDate &&
    (useRevalIdFlow || flightData);

  useEffect(() => {
    if (contextFareRules && contextFareRules.success) {
      setData(contextFareRules as FareRulesResult);
      setError(null);
      return;
    }
    if (contextFareRules && !contextFareRules.success && contextFareRules.error) {
      setData(contextFareRules as FareRulesResult);
      setError(contextFareRules.error);
      return;
    }
    setData(null);
    setError(null);
  }, [contextFareRules]);

  useEffect(() => {
    setFetched(false);
    setData(null);
    setError(null);
  }, [revalId]);

  const doFetch = () => {
    if (!canFetch || fetched || contextFareRules !== null) return;
    setLoading(true);
    setError(null);
    const reqDep = derivedDep || departureDate || "";
    const reqRet = derivedRet ?? returnDate;

    const payload: Record<string, unknown> = {
      userId,
      sessionId,
      revalId,
      passengerCounts,
      departureDate: reqDep,
      returnDate: reqRet,
      currency,
    };

    const url = `${process.env.REACT_APP_API_URL ?? ""}/api/flights/fare-rules`;
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "include",
      body: JSON.stringify(payload),
    })
      .then(async (res) => {
        const body = await res.json();
        setFetched(true);
        setFareRules(body);
        setData(body);
        const msg = (body as { errors?: { message?: string }[] })?.errors?.[0]?.message ?? (body as { error?: string }).error;
        if (!res.ok) setError(msg ?? "Unable to load fare rules.");
        else setError(body.success ? null : (msg ?? "Unable to load fare rules."));
      })
      .catch(() => {
        setFetched(true);
        setError("Unable to load fare rules.");
        setData(null);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (displayMode !== "popup" || !popupOpen) return;
    if (contextFareRules !== null) return;
    if (!canFetch || fetched) return;
    doFetch();
  }, [displayMode, popupOpen, canFetch, fetched, contextFareRules]);


  const renderPenalty = (label: string, info?: PenaltyInfo) => {
    if (!info) return null;
    return (
      <div className="space-y-2 text-sm text-black">
        <div className="flex justify-between font-medium">
          <span>{label}</span>
          <span>
            {info.allowed ? "Allowed" : "Not allowed"}
            {info.amount != null && info.amount > 0 && ` (${info.currency ?? "AED"} ${info.amount})`}
          </span>
        </div>
        {info.description && (
          <p className="text-xs text-gray-600">{info.description}</p>
        )}
        {info.penaltyBreakdown && info.penaltyBreakdown.length > 0 && (
          <ul className="mt-2 space-y-1 text-xs">
            {info.penaltyBreakdown.map((p, i) => (
              <li key={i} className="flex justify-between">
                <span>
                  {p.dateRange}
                  {p.timeRange ? ` · ${p.timeRange}` : ""}
                </span>
                <span>
                  {p.currency} {p.amount}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const renderContent = () => {
    if (loading && !data) {
      return (
        <div className="p-6 text-center">
          <p className="text-sm text-gray-500">Loading fare rules...</p>
        </div>
      );
    }
    if (error || (data && !data.success)) {
      return (
        <div className="p-6">
          <p className="text-sm text-gray-600">
            {error || data?.error || "Fare rules are not available for this flight."}
          </p>
        </div>
      );
    }
    if (!data || !data.success) return null;
    return (
      <div className="p-6 space-y-6">
        <div>
          <h3 className="font-semibold text-primary border-b pb-2 mb-2">Date change</h3>
          {renderPenalty("Change flight date", data.exchange)}
          {!data.exchange && (
            <p className="text-sm text-gray-600">No date change information available.</p>
          )}
        </div>
        <div>
          <h3 className="font-semibold text-primary border-b pb-2 mb-2">Cancellation</h3>
          {renderPenalty("Refund / cancel", data.refund)}
          {!data.refund && (
            <p className="text-sm text-gray-600">No cancellation information available.</p>
          )}
        </div>
      </div>
    );
  };

  if (displayMode === "popup") {
    return (
      <div className={className}>
        <button
          type="button"
          onClick={() => setPopupOpen(true)}
          className="flex items-center gap-2 w-full justify-center rounded-xl py-2.5 px-4 bg-[#F5F8FF] border border-[#E4E4E4] hover:bg-[#E8EEFF] transition text-sm font-medium text-primary"
        >
          <FaRegClipboard className="text-blue-800" />
          <span>View fare rules</span>
        </button>
        {popupOpen && (
          <div
            className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/40"
            onClick={() => setPopupOpen(false)}
            role="dialog"
            aria-modal="true"
            aria-label="Fare rules"
          >
            <div
              className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[85vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center border-b p-4 bg-[#F5F8FF]">
                <h2 className="text-lg font-sora font-semibold text-black">Fare rules</h2>
                <button
                  type="button"
                  onClick={() => setPopupOpen(false)}
                  className="text-gray-500 hover:text-black text-xl leading-none"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="overflow-y-auto flex-1">{renderContent()}</div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (!canFetch && !contextFareRules) return null;

  if (loading && !data) {
    return (
      <div className={`rounded-xl p-4 bg-[#F5F8FF] border border-[#E4E4E4] ${className}`}>
        <div className="flex justify-between items-center border-b pb-2 mb-2 text-subheading font-sora text-black">
          <span>Fare rules</span>
          <FaRegClipboard className="text-blue-800" />
        </div>
        <p className="text-sm text-gray-500">Loading policy...</p>
      </div>
    );
  }

  if (error || (data && !data.success)) {
    return (
      <div className={`rounded-xl p-4 bg-[#F5F8FF] border border-[#E4E4E4] ${className}`}>
        <div className="flex justify-between items-center border-b pb-2 mb-2 text-subheading font-sora text-black">
          <span>Fare rules</span>
          <FaRegClipboard className="text-blue-800" />
        </div>
        <p className="text-sm text-gray-600">
          {error || (data as { errors?: { message?: string }[] })?.errors?.[0]?.message || (data as { error?: string })?.error || "Fare rules are not available."}
        </p>
      </div>
    );
  }

  if (!data || !data.success) return null;

  if (variant === "compact") {
    return (
      <div className={`rounded-xl p-4 bg-[#F5F8FF] border border-[#E4E4E4] ${className}`}>
        <div className="flex justify-between items-center border-b pb-2 mb-2 text-subheading font-sora text-black">
          <span>Fare rules</span>
          <FaRegClipboard className="text-blue-800" />
        </div>
        <div className="space-y-4">
          {renderPenalty("Date change", data.exchange)}
          {renderPenalty("Cancellation", data.refund)}
        </div>
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${className}`}>
      <div className="bg-[#F5F8FF] rounded-xl p-6">
        <div className="flex justify-between items-center border-b pb-2 mb-2 text-subheading font-sora text-black">
          <span>Date change charges</span>
          <FaRegClipboard className="text-blue-800" />
        </div>
        {renderPenalty("Exchange", data.exchange)}
      </div>
      <div className="bg-[#F5F8FF] rounded-xl p-6">
        <div className="flex justify-between items-center border-b pb-2 mb-2 text-subheading font-sora text-black">
          <span>Cancellation charges</span>
          <FaRegClipboard className="text-blue-800" />
        </div>
        {renderPenalty("Refund", data.refund)}
      </div>
    </div>
  );
};

export default FareRulesDisplay;
